import type { RouteRecordRaw } from 'vue-router';

import { useAppStore } from '/@/store/modules/app';
import { usePermissionStore } from '/@/store/modules/permission';
import { useUserStore } from '/@/store/modules/user';

import { useTabs } from './useTabs';

import { router, resetRouter } from '/@/router';
// import { RootRoute } from '/@/router/routes';

import projectSetting from '/@/settings/projectSetting';
import { PermissionModeEnum } from '/@/enums/appEnum';
import { RoleEnum } from '/@/enums/roleEnum';

import { intersection } from 'lodash-es';
import { isArray } from '/@/utils/is';
import { useMultipleTabStore } from '/@/store/modules/multipleTab';

// User permissions related operations
export function usePermission() {
  const userStore = useUserStore();
  const appStore = useAppStore();
  const permissionStore = usePermissionStore();
  const { closeAll } = useTabs(router);

  /**
   * Change permission mode
   */
  async function togglePermissionMode() {
    appStore.setProjectConfig({
      permissionMode:
        projectSetting.permissionMode === PermissionModeEnum.BACK
          ? PermissionModeEnum.ROUTE_MAPPING
          : PermissionModeEnum.BACK,
    });
    location.reload();
  }

  /**
   * Reset and regain authority resource information
   * @param id
   */
  async function resume() {
    const tabStore = useMultipleTabStore();
    tabStore.clearCacheTabs();
    resetRouter();
    const routes = await permissionStore.buildRoutesAction();
    routes.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw);
    });
    permissionStore.setLastBuildMenuTime();
    closeAll();
  }

  /**
   * 确定是否存在权限
   */
  function hasPermission(value?: RoleEnum | RoleEnum[] | string | string[], def = true): boolean {
    // Visible by default
    if (!value) {
      return def;
    }

    const permMode = projectSetting.permissionMode;

    if ([PermissionModeEnum.ROUTE_MAPPING, PermissionModeEnum.ROLE].includes(permMode)) {
      if (!isArray(value)) {
        return userStore.getRoleList?.includes(value as RoleEnum);
      }
      return (intersection(value, userStore.getRoleList) as RoleEnum[]).length > 0;
    }

    if (PermissionModeEnum.BACK === permMode) {
      const allCodeList = permissionStore.getPermCodeList as string[];
      if (!isArray(value)&&allCodeList&&allCodeList.length>0) {
        return allCodeList.includes(value);
      }
      return (intersection(value, allCodeList) as string[]).length > 0;
    }
    return true;
  }

  /**
   * 合并「角色权限码」与「单据级管理人员授权（managerAuthList）」的权限判断，二者命中其一即视为有权限
   * @param value 权限码，与 hasPermission 一致
   * @param managerAuthList 接口返回的管理人员授权列表，形如 [{ action, type, describe }]，可能为 null 或空数组
   */
  function hasPermissionList(value?: RoleEnum | RoleEnum[] | string | string[], managerAuthList?: null | { action?: string }[], def = true): boolean {
    // 先按全局权限码判断
    if (hasPermission(value, def)) {
      return true;
    }
    if (isArray(value) ? value.length === 0 : !value) {
      return false;
    }
    if (!isArray(managerAuthList) || managerAuthList.length === 0) {
      return false;
    }
    // 再按当前单据的管理人员授权判断
    const authCodeList = managerAuthList.map((item) => item?.action).filter((action) => !!action);
    const valueList = (isArray(value) ? value : [value]) as string[];
    return valueList.some((code) => authCodeList.includes(code));
  }

  /**
   * 租户是否有权限
   */
  function hasTenantPermission(value: string, def = true): boolean {
    // Visible by default
    if (!value) {
      return def;
    }
    const allCodeList = permissionStore.getTenantConfig as string[];
    if (allCodeList?.length > 0) {
      return allCodeList.some((item) => item.modelKey === value);
    }
    return false
  }

  /**
   * 获取租户字段配置
   */
  function getTenantFieldConfig(fieldKey: string) {
    if (!fieldKey) {
      return null;
    }
    const fieldConfigList = userStore.getTenantFieldConfigList as any[];
    return fieldConfigList?.find((item) => item.fieldKey === fieldKey) || null;
  }

  /**
   * 租户字段是否显示，isHidden=0 显示
   */
  function hasTenantFieldVisible(fieldKey: string, def = true): boolean {
    const fieldConfig = getTenantFieldConfig(fieldKey);
    if (!fieldConfig) {
      return def;
    }
    return `${fieldConfig.isHidden}` === '0';
  }

  /**
   * 租户字段是否必填，isRequired=1 必填
   */
  function hasTenantFieldRequired(fieldKey: string, def = false): boolean {
    const fieldConfig = getTenantFieldConfig(fieldKey);
    if (!fieldConfig) {
      return def;
    }
    return `${fieldConfig.isRequired}` === '1';
  }

  /**
   * 是否禁用组件
   */
  function isDisabledAuth(value?: RoleEnum | RoleEnum[] | string | string[], def = true): boolean {
    return !hasPermission(value);
  }

  function hasTagPermission(value): boolean {
    const allTagList = permissionStore.getTagList || {};
    return !!allTagList[value];
  }

  /**
   * Change roles
   * @param roles
   */
  async function changeRole(roles: RoleEnum | RoleEnum[]): Promise<void> {
    if (projectSetting.permissionMode !== PermissionModeEnum.ROUTE_MAPPING) {
      throw new Error(
        'Please switch PermissionModeEnum to ROUTE_MAPPING mode in the configuration to operate!'
      );
    }

    if (!isArray(roles)) {
      roles = [roles];
    }
    userStore.setRoleList(roles);
    await resume();
  }

  /**
   * refresh menu data
   */
  async function refreshMenu() {
    resume();
  }

  function notTemporaryDisabledAuth(){
    return !userStore.getUserConfig?.hidePermissionFlag?.length>0;
  }


  return {
    changeRole,
    hasPermission,
    hasPermissionList,
    togglePermissionMode,
    refreshMenu,
    isDisabledAuth,
    hasTagPermission,
    notTemporaryDisabledAuth,
    hasTenantPermission,
    getTenantFieldConfig,
    hasTenantFieldVisible,
    hasTenantFieldRequired,
  };
}
